/**
 * HistogramPanel — raster band statistics card.
 *
 * Status (WP-3): **placeholder**. The masterplan §7.2 lists
 * min / max / mean / std / percentiles, but a charting library is NOT yet
 * in the dependency manifest. Per the WP-3 prompt blocker rule:
 *
 *   > "If `<HistogramPanel>` would require a charting library that isn't
 *   >  already in the repo, ship a placeholder card with `info` icon and
 *   >  a 'Histogram coming with WP-9 data tools' note. Do NOT add a new
 *   >  dep."
 *
 * The real chart will land alongside the WP-9 data tools so this surface
 * is wired into the Raster ToolCard today and can drop in a chart body
 * later without API changes for any consumer.
 *
 * The placeholder still surfaces band metadata (count, names) when the
 * raster has been picked, so the Card carries enough context to avoid
 * looking empty.
 */

import { Callout, Chip, tokens } from '@/design';
import { useViewerSlice } from '@/state/useViewerSlice';

export interface HistogramPanelProps {
  /** When true, the panel renders even outside the Raster ToolCard. */
  forceVisible?: boolean;
  'data-testid'?: string;
}

export function HistogramPanel({
  forceVisible,
  'data-testid': testId,
}: HistogramPanelProps) {
  const sample = useViewerSlice((s) => s.rasterToolCard.lastSample);
  const bandCount = sample?.values.length ?? 0;
  const bandNames = sample?.bandNames ?? [];

  return (
    <div
      data-testid={testId ?? 'raster-histogram-panel'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
      }}
    >
      <Callout
        severity="info"
        icon="info"
        title="Histogram preview"
      >
        Histogram coming with WP-9 data tools. Pixel and band metadata
        below are live; min / max / std / percentiles will land alongside
        the data-tools work package.
      </Callout>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: tokens.space[2],
        }}
      >
        <Chip
          icon="query_stats"
          variant="neutral"
          label={`${bandCount} bands`}
        />
        {forceVisible || bandCount > 0
          ? bandNames.slice(0, 6).map((name) => (
              <Chip key={name} variant="neutral" label={name} />
            ))
          : (
            <Chip
              icon="block"
              variant="neutral"
              label="No pixel sampled"
            />
          )}
      </div>
    </div>
  );
}
